import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Move } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  generateCroppedUrl,
  generateAnnotatedUrl,
  generateCroppedAndAnnotatedUrl,
  calculateCropFromPosition,
  type ViewerCropState,
  type MarkerPosition as CloudinaryMarkerPosition,
} from '@/utils/cloudinaryComposite';

interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
}

interface MarkerPosition {
  x: number;
  y: number;
}

export interface SearchImageViewerState {
  viewport: ViewportState;
  marker: MarkerPosition;
  imageUrl: string;
  annotatedUrl: string;
  croppedUrl: string;
  croppedAndAnnotatedUrl: string;
  imageSize: { width: number; height: number };
  containerSize: { width: number; height: number };
  cropCenter: { x: number; y: number };
}

// Re-export utility functions for backwards compatibility
export {
  generateCroppedUrl,
  generateAnnotatedUrl,
  generateCroppedAndAnnotatedUrl,
  calculateCropFromPosition,
} from '@/utils/cloudinaryComposite';

interface SearchImageViewerProps {
  imageUrl: string;
  initialMarkerX?: number;
  initialMarkerY?: number;
  initialViewportCenterX?: number;
  initialViewportCenterY?: number;
  initialZoom?: number;
  initialPanX?: number;
  initialPanY?: number;
  showMarker?: boolean;  // Whether to show the marker (default: true)
  onStateChange?: (state: SearchImageViewerState) => void;
  className?: string;
}

const BASE_SCALE = 0.75;
const DEFAULT_ZOOM = 1.0;

export function SearchImageViewer({
  imageUrl,
  initialMarkerX = 0,
  initialMarkerY = 0,
  initialViewportCenterX,
  initialViewportCenterY,
  initialZoom,
  initialPanX,
  initialPanY,
  showMarker = true,
  onStateChange,
  className = '',
}: SearchImageViewerProps) {
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [zoomLevel, setZoomLevel] = useState(initialZoom ?? DEFAULT_ZOOM);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: initialPanX ?? 0, y: initialPanY ?? 0 });
  const [markerPos, setMarkerPos] = useState<MarkerPosition>({ x: initialMarkerX, y: initialMarkerY });
  const [isPanning, setIsPanning] = useState(false);
  const [isMarkerDragging, setIsMarkerDragging] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  // Use refs for immediate access during drag operations (avoid React state update delay)
  const isMarkerDraggingRef = useRef(false);
  const isPanningRef = useRef(false);
  const [initialized, setInitialized] = useState(false);
  const [minZoom, setMinZoom] = useState(0.1);
  const [isMarkerPulsing, setIsMarkerPulsing] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Viewport center is INDEPENDENT of marker position
  // Use explicit viewport center if provided, otherwise default to marker position (first-time viewing)
  const [viewportCenter, setViewportCenter] = useState<{ x: number; y: number }>({
    x: initialViewportCenterX ?? initialMarkerX,
    y: initialViewportCenterY ?? initialMarkerY,
  });

  // Keep refs in sync for use in event handlers (avoid stale closures)
  const viewportCenterRef = useRef(viewportCenter);
  const panOffsetRef = useRef(panOffset);
  const zoomLevelRef = useRef(zoomLevel);

  useEffect(() => {
    viewportCenterRef.current = viewportCenter;
  }, [viewportCenter]);

  useEffect(() => {
    panOffsetRef.current = panOffset;
  }, [panOffset]);

  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  // Measure container size
  useEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };
    measureContainer();
    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, []);

  // Load image to get dimensions
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Reset state when image changes
  useEffect(() => {
    setMarkerPos({ x: initialMarkerX, y: initialMarkerY });
    setViewportCenter({
      x: initialViewportCenterX ?? initialMarkerX,
      y: initialViewportCenterY ?? initialMarkerY,
    });
    setPanOffset({ x: initialPanX ?? 0, y: initialPanY ?? 0 });
    setZoomLevel(initialZoom ?? DEFAULT_ZOOM);
    setInitialized(false);
    // Only pulse if no saved state (first time viewing)
    setIsMarkerPulsing(initialZoom === undefined);
  }, [imageUrl, initialMarkerX, initialMarkerY, initialViewportCenterX, initialViewportCenterY, initialZoom, initialPanX, initialPanY]);

  // Stop marker pulsing after 1 second
  useEffect(() => {
    if (isMarkerPulsing && initialized) {
      const timer = setTimeout(() => setIsMarkerPulsing(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isMarkerPulsing, initialized]);


  // Calculate minimum zoom
  useEffect(() => {
    if (imageSize && containerSize.width > 0) {
      const fitZoomX = containerSize.width / (imageSize.width * BASE_SCALE);
      const fitZoomY = containerSize.height / (imageSize.height * BASE_SCALE);
      const fitZoom = Math.min(fitZoomX, fitZoomY);
      setMinZoom(Math.max(0.05, fitZoom));
    }
  }, [imageSize, containerSize]);

  // Initialize - center on marker
  useEffect(() => {
    if (imageSize && containerSize.width > 0 && !initialized) {
      setInitialized(true);
    }
  }, [imageSize, containerSize, initialized]);

  // Notify parent of state changes
  useEffect(() => {
    if (onStateChange && initialized && imageSize) {
      // Build viewer state for Cloudinary URL generation
      const viewerCropState: ViewerCropState = {
        imageWidth: imageSize.width,
        imageHeight: imageSize.height,
        containerWidth: containerSize.width,
        containerHeight: containerSize.height,
        zoom: zoomLevel,
        panX: panOffset.x,
        panY: panOffset.y,
        cropCenterX: viewportCenter.x,
        cropCenterY: viewportCenter.y,
      };

      const marker: CloudinaryMarkerPosition = { x: markerPos.x, y: markerPos.y };

      // Generate all URL variants using the utility functions
      const annotatedUrl = generateAnnotatedUrl(imageUrl, marker);
      const croppedUrl = generateCroppedUrl(imageUrl, viewerCropState);
      const croppedAndAnnotatedUrl = generateCroppedAndAnnotatedUrl(imageUrl, viewerCropState, marker);

      onStateChange({
        viewport: {
          zoom: zoomLevel,
          panX: panOffset.x,
          panY: panOffset.y,
        },
        marker: markerPos,
        imageUrl,
        annotatedUrl,
        croppedUrl,
        croppedAndAnnotatedUrl,
        imageSize,
        containerSize,
        cropCenter: viewportCenter,
      });
    }
  }, [zoomLevel, panOffset, markerPos, imageUrl, onStateChange, initialized, imageSize, containerSize, viewportCenter]);

  const isReady = imageSize && containerSize.width > 0 && initialized;

  // Calculate scaled dimensions
  const scale = BASE_SCALE * zoomLevel;
  const scaledWidth = imageSize ? imageSize.width * scale : 0;
  const scaledHeight = imageSize ? imageSize.height * scale : 0;
  const viewWidth = containerSize.width || 300;
  const viewHeight = containerSize.height || 300;

  // Calculate crop position centered on viewportCenter (independent of marker position)
  const scaledViewportCenterX = viewportCenter.x * scale;
  const scaledViewportCenterY = viewportCenter.y * scale;
  const baseCropX = Math.max(0, scaledViewportCenterX - viewWidth / 2);
  const baseCropY = Math.max(0, scaledViewportCenterY - viewHeight / 2);

  // Calculate scaled marker position for display
  const scaledMarkerX = markerPos.x * scale;
  const scaledMarkerY = markerPos.y * scale;

  // Apply pan offset
  let cropX = baseCropX - panOffset.x;
  let cropY = baseCropY - panOffset.y;

  // Clamp to image bounds
  const maxScrollX = Math.max(0, scaledWidth - viewWidth);
  const maxScrollY = Math.max(0, scaledHeight - viewHeight);
  cropX = Math.max(0, Math.min(cropX, maxScrollX));
  cropY = Math.max(0, Math.min(cropY, maxScrollY));

  // Calculate marker position in viewport
  const markerViewportX = scaledMarkerX - cropX;
  const markerViewportY = scaledMarkerY - cropY;

  // Calculate horizontal and vertical offsets when image is centered
  const imageOffsetX = scaledWidth < viewWidth ? (viewWidth - scaledWidth) / 2 : 0;
  const imageOffsetY = scaledHeight < viewHeight ? (viewHeight - scaledHeight) / 2 : 0;

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.25, minZoom));
  }, [minZoom]);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(DEFAULT_ZOOM);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't start panning if clicking on marker or already dragging marker
    if ((e.target as HTMLElement).closest('.annotation-marker') || isMarkerDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    isPanningRef.current = true;
    setIsPanning(true);
    setPanStart({
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset.x,
      offsetY: panOffset.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Marker dragging takes priority - use ref for immediate check
    // Skip all processing here - document-level listener handles marker drag
    if (isMarkerDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return; // Don't process anything - document listener handles marker position
    }

    // Panning - only if panning and not dragging marker
    if (!isPanningRef.current || !imageSize) return;

    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;

    const minPanX = baseCropX - maxScrollX;
    const maxPanX = baseCropX;
    const minPanY = baseCropY - maxScrollY;
    const maxPanY = baseCropY;

    const newOffsetX = Math.max(minPanX, Math.min(maxPanX, panStart.offsetX + dx));
    const newOffsetY = Math.max(minPanY, Math.min(maxPanY, panStart.offsetY + dy));

    setPanOffset({ x: newOffsetX, y: newOffsetY });
  };

  const handleMouseUp = () => {
    // Only handle pan end here - marker drag is handled by document listener
    if (isPanningRef.current) {
      isPanningRef.current = false;
      setIsPanning(false);
    }
  };

  const handleMouseLeave = () => {
    // Only handle pan end here - marker drag continues via document listener
    if (isPanningRef.current) {
      isPanningRef.current = false;
      setIsPanning(false);
    }
  };

  const handleMarkerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Immediately set refs to prevent any panning
    isPanningRef.current = false;
    isMarkerDraggingRef.current = true;
    setIsPanning(false);
    setIsMarkerDragging(true);

    // Attach document-level listeners for drag - these capture all mouse events
    const handleDocumentMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();

      if (!isMarkerDraggingRef.current || !imageSize || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = moveEvent.clientX - rect.left;
      const mouseY = moveEvent.clientY - rect.top;

      // Calculate current scale and offsets using refs (avoid stale closure values)
      const currentScale = BASE_SCALE * zoomLevelRef.current;
      const currentScaledWidth = imageSize.width * currentScale;
      const currentScaledHeight = imageSize.height * currentScale;
      const currentImageOffsetX = currentScaledWidth < containerSize.width
        ? (containerSize.width - currentScaledWidth) / 2
        : 0;
      const currentImageOffsetY = currentScaledHeight < containerSize.height
        ? (containerSize.height - currentScaledHeight) / 2
        : 0;

      // Recalculate crop position using current viewport center
      const currentScaledViewportCenterX = viewportCenterRef.current.x * currentScale;
      const currentScaledViewportCenterY = viewportCenterRef.current.y * currentScale;
      const currentBaseCropX = Math.max(0, currentScaledViewportCenterX - containerSize.width / 2);
      const currentBaseCropY = Math.max(0, currentScaledViewportCenterY - containerSize.height / 2);
      const currentMaxScrollX = Math.max(0, currentScaledWidth - containerSize.width);
      const currentMaxScrollY = Math.max(0, currentScaledHeight - containerSize.height);
      let currentCropX = currentBaseCropX - panOffsetRef.current.x;
      let currentCropY = currentBaseCropY - panOffsetRef.current.y;
      currentCropX = Math.max(0, Math.min(currentCropX, currentMaxScrollX));
      currentCropY = Math.max(0, Math.min(currentCropY, currentMaxScrollY));

      const adjustedMouseX = mouseX - currentImageOffsetX;
      const adjustedMouseY = mouseY - currentImageOffsetY;
      const imagePixelX = (adjustedMouseX + currentCropX) / currentScale;
      const imagePixelY = (adjustedMouseY + currentCropY) / currentScale;

      const clampedX = Math.max(0, Math.min(imagePixelX, imageSize.width));
      const clampedY = Math.max(0, Math.min(imagePixelY, imageSize.height));

      setMarkerPos({ x: Math.round(clampedX), y: Math.round(clampedY) });
    };

    const handleDocumentMouseUp = () => {
      isMarkerDraggingRef.current = false;
      setIsMarkerDragging(false);

      // Clean up listeners
      document.removeEventListener('mousemove', handleDocumentMouseMove, true);
      document.removeEventListener('mouseup', handleDocumentMouseUp, true);
    };

    // Use capture phase to intercept events before they reach other elements
    document.addEventListener('mousemove', handleDocumentMouseMove, true);
    document.addEventListener('mouseup', handleDocumentMouseUp, true);
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-gray-900 cursor-grab active:cursor-grabbing ${className}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {!isReady ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading...</div>
        </div>
      ) : (
        <>
          {/* Zoom controls */}
          <div className="absolute top-3 right-3 z-20 flex gap-1 bg-black/60 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handleZoomIn}
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handleZoomOut}
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={handleZoomReset}
              title="Reset view"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>

          {/* Zoom indicator */}
          <div className="absolute top-3 left-3 z-20 bg-black/60 text-white text-xs px-2 py-1 rounded">
            {Math.round(zoomLevel * 100)}%
          </div>

          {/* Drag hint */}
          <div className="absolute bottom-3 left-3 z-20 flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-black/60 text-white/70">
            <Move className="h-3 w-3" />
            <span>{showMarker ? 'Drag to pan • Drag marker to reposition' : 'Drag to pan'}</span>
          </div>

          {/* Image - centered horizontally when smaller than container */}
          <div
            className="absolute select-none z-0"
            style={{
              width: `${scaledWidth}px`,
              height: `${scaledHeight}px`,
              left: scaledWidth < viewWidth ? `${(viewWidth - scaledWidth) / 2}px` : undefined,
              transform: `translate(${scaledWidth < viewWidth ? 0 : -cropX}px, ${-cropY}px)`,
            }}
          >
            <img
              src={imageUrl}
              alt="Screenshot"
              crossOrigin="anonymous"
              className="select-none pointer-events-none block w-full h-full"
              draggable={false}
            />
          </div>

          {/* Marker - arrow style matching diffs lightbox */}
          {showMarker && (
            <div
              className="annotation-marker absolute z-10 cursor-move"
              style={{
                left: Math.max(0, Math.min(viewWidth - 40, imageOffsetX + markerViewportX - 20)),
                top: Math.max(8, Math.min(viewHeight - 32, imageOffsetY + markerViewportY - 12)),
              }}
              onMouseDown={handleMarkerMouseDown}
            >
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-white shadow-lg transition-transform ${
                  isMarkerDragging ? 'ring-2 ring-blue-400 scale-110' : 'hover:scale-105'
                }`}
                style={{
                  backgroundColor: '#aebb36',
                  animation: isMarkerPulsing ? 'slideInPulse 1s ease-out' : undefined,
                }}
              >
                <span className="text-[10px] opacity-75">→</span>
              </div>
            </div>
          )}

          {/* Keyframe animation for marker intro */}
          <style>{`
            @keyframes slideInPulse {
              0% {
                transform: translateX(-50px);
                opacity: 0;
              }
              30% {
                transform: translateX(0);
                opacity: 1;
              }
              45% {
                transform: scale(1.2);
              }
              60% {
                transform: scale(0.95);
              }
              75% {
                transform: scale(1.1);
              }
              90% {
                transform: scale(0.98);
              }
              100% {
                transform: scale(1);
              }
            }
          `}</style>
        </>
      )}
    </div>
  );
}

export default SearchImageViewer;
